import { EventEmitter, Injectable } from '@angular/core';
import { IServiceInfo, IServiceWizardQuickCreation, IServiceWizardSetup, IServiceWizardSetupPayload } from 'src/app/models';
import { NotificationTypes, PhaseStatus, ServiceType, ServiceWizardStep } from 'src/app/models/enums';
import { ErrorsCode } from 'src/app/models/enums/errorsCode';
import { images } from 'src/images';
import { objectToCamel } from 'ts-case-convert';
import { AuthService } from '../auth/auth.service';
import { LiteralService } from '../literal/literal.service';
import { RouteService } from '../routes/route.service';
import { UtilsService } from '../utils/utils.service';
import { WebSocketService } from '../web-socket/web-socket.service';

@Injectable({ providedIn: 'root' })
export class ServiceWizardService {

  public active = false;
  public step = ServiceWizardStep.SERVICE;
  public roundTrip = false;
  public canContinueEvaluator: () => boolean = () => false;
  public continueLabelKey = 'continue';
  public readonly continueEventEmitter = new EventEmitter<void>();
  public readonly backEventEmitter = new EventEmitter<void>();
  public pendingQuickCreation: IServiceWizardQuickCreation | null = null;
  public pendingWizardSetup: IServiceWizardSetup | null = null;
  public pendingWizardBackwardsSetup: IServiceWizardSetup | null = null;

  constructor(private routeService: RouteService,
              private utilsService: UtilsService,
              private literalService: LiteralService,
              private authService: AuthService,
              private webSocketService: WebSocketService) { }

  get canContinue(): boolean {
    return this.canContinueEvaluator();
  }

  get steps(): ServiceWizardStep[] {
    return this.roundTrip
      ? [ServiceWizardStep.SERVICE, ServiceWizardStep.FORWARD_ROUTE, ServiceWizardStep.BACKWARDS_ROUTE, ServiceWizardStep.FORWARD_SETUP, ServiceWizardStep.BACKWARDS_SETUP]
      : [ServiceWizardStep.SERVICE, ServiceWizardStep.FORWARD_ROUTE, ServiceWizardStep.FORWARD_SETUP];
  }

  get isFirstStep(): boolean {
    return this.step === ServiceWizardStep.SERVICE;
  }

  get isLastStep(): boolean {
    return this.steps[this.steps.length - 1] === this.step;
  }

  get isSetupStep(): boolean {
    return this.step === ServiceWizardStep.FORWARD_SETUP || this.step === ServiceWizardStep.BACKWARDS_SETUP;
  }

  isPreviousToCurrentStep(step: ServiceWizardStep): boolean {
    const currentStepIndex = this.steps.indexOf(this.step);
    return this.steps.indexOf(step) < currentStepIndex;
  }
  
  stepLabelKey(step: ServiceWizardStep): string {
    if (!this.roundTrip && step === ServiceWizardStep.FORWARD_ROUTE) return 'route';
    if (!this.roundTrip && step === ServiceWizardStep.FORWARD_SETUP) return 'setup';
    return step;
  }

  start() {
    this.active = true;
    this.step = ServiceWizardStep.SERVICE;
    this.roundTrip = false;
    this.pendingQuickCreation = null;
    this.pendingWizardSetup = null;
    this.pendingWizardBackwardsSetup = null;
    this.continueLabelKey = 'continue';
  }

  setStep(step: ServiceWizardStep) {
    this.step = step;
  }

  // Reactivation while navigating between wizard steps: unlike start(), the pending
  // creation and setup data are kept
  restore(step: ServiceWizardStep) {
    this.active = true;
    this.step = step;
    this.roundTrip = !!this.pendingQuickCreation?.serviceInfo?.roundTrips;
  }

  stop() {
    // pendingQuickCreation is kept: it survives page changes so the wizard can be
    // restored when coming back from the setup screen
    this.active = false;
    this.step = ServiceWizardStep.SERVICE;
    this.canContinueEvaluator = () => false;
    this.continueLabelKey = 'continue';
  }

  async submitQuickCreation(
    setup: IServiceWizardSetupPayload | null,
    backwardsSetup: IServiceWizardSetupPayload | null = null,
    onProgress?: (percentage: number, detail: string) => void,
  ): Promise<IServiceInfo | null> {
    const pending = this.pendingQuickCreation;
    if (!pending) return null;
    const serviceInfo = pending.serviceInfo;
    const discretionary = serviceInfo.serviceType === ServiceType.DISCRETIONARY;
    const roundTrip = !!serviceInfo.roundTrips;
    const payload: any = {
      town: serviceInfo.townId > 0
        ? { id: serviceInfo.townId, clientId: serviceInfo.clientId }
        : { clientId: serviceInfo.clientId },
      serviceInfo: {
        name: discretionary || !serviceInfo.name ? serviceInfo.tag : serviceInfo.name,
        tag: serviceInfo.tag,
        serviceType: serviceInfo.serviceType,
        roundTrip,
        forwardDirection: serviceInfo.forwardDirection || null,
        backwardsDirection: serviceInfo.backwardsDirection || null,
        color: serviceInfo.color || null,
        sortCriteria: serviceInfo.sortCriteria,
      },
      service: this.buildServicePayload(pending.route, serviceInfo.clientId),
      setup: this.buildSetupPayload(setup),
      backwardsService: roundTrip && pending.backwardsRoute ? this.buildServicePayload(pending.backwardsRoute, serviceInfo.clientId) : null,
      backwardsSetup: roundTrip ? this.buildSetupPayload(backwardsSetup) : null,
    };
    try {
      const operationId = await this.routeService.createQuickService(payload);
      const ephemeralToken = await this.authService.getEphemeralToken() as string;
      return await this.trackQuickCreation(operationId, ephemeralToken, onProgress);
    } catch (error: any) {
      this.showQuickCreationError(error);
      return null;
    }
  }

  private trackQuickCreation(
    operationId: string,
    ephemeralToken: string,
    onProgress?: (percentage: number, detail: string) => void,
  ): Promise<IServiceInfo | null> {
    this.webSocketService.connect(operationId, ephemeralToken);
    return new Promise((resolve) => {
      this.webSocketService.getMessages().subscribe({
        next: (message: any) => {
          onProgress?.(message.total_percentage, this.webSocketService.progressDetail(message));
          if (this.webSocketService.isIntermediatePhaseCompletion(message)) return;
          switch (message.phase_status) {
            case PhaseStatus.COMPLETED:
              resolve(this.finishQuickCreation(message.progress_entity_data));
              break;
            case PhaseStatus.FAILED:
              const error = this.utilsService.isJSON(message.progress_entity_data)
                ? JSON.parse(message.progress_entity_data)
                : undefined;
              this.showQuickCreationError(error);
              resolve(null);
              break;
          }
        },
        error: () => {
          this.utilsService.showNotification(images.notifications.warning, 'services.actions.createService', NotificationTypes.DANGER);
          resolve(null);
        },
      });
    });
  }

  private finishQuickCreation(result: any): IServiceInfo | null {
    const response: any = objectToCamel(result);
    this.utilsService.showNotification(images.notifications.check, 'services.actions.createService', NotificationTypes.SUCCESS);
    this.pendingQuickCreation = null;
    this.pendingWizardSetup = null;
    this.pendingWizardBackwardsSetup = null;
    return response?.serviceInfo ?? null;
  }

  private showQuickCreationError(error: any) {
    if (error?.error_code) {
      const title = this.literalService.get(`${error.error_code}_ERROR.title`, true);
      let text = this.literalService.get(`${error.error_code}_ERROR.text`, true);
      switch (error.error_code) {
        case ErrorsCode.START_OR_END_ON_DYNAMIC:
          text = this.literalService.get(`${error.error_code}_ERROR.${error.entity_info}Text`, true);
          break;
        case ErrorsCode.INVALID_REQUEST:
          text = this.literalService.get(`${error.error_code}_ERROR.text`, true, { fieldName: error.entity_info });
          break;
      }
      this.utilsService.showNotification(images.notifications.warning, '', NotificationTypes.DANGER, title, text, undefined, false);
    } else {
      this.utilsService.showNotification(images.notifications.warning, 'services.actions.createService', NotificationTypes.DANGER);
    }
  }

  private buildServicePayload(route: any, managerClientId: number) {
    return {
      terrain: route.terrain,
      outline: route.outline ?? null,
      name: route.name || null,
      managerClientId,
      maxTime: route.maxTime || null,
      expeditionType: route.expeditionType || null,
      visibility: route.visibility || null,
      showBusPosition: route.showBusPosition ?? null,
      showUserDataToDriver: route.showUserDataToDriver ?? null,
      singleStopBookings: route.singleStopBookings ?? null,
      criteria: route.criteria || null,
      maxMergeDistance: route.maxMergeDistance ?? null,
      ticketingType: route.ticketingType || null,
      sms: route.sms ?? null,
      push: route.push ?? null,
      email: route.email ?? null,
      whatsapp: route.whatsapp ?? null,
      informative: route.informative ?? null,
      dynamicStartStop: route.dynamicStartStop ?? null,
      dynamicEndStop: route.dynamicEndStop ?? null,
      apps: route.apps ?? [],
      fare: route.fare ?? null,
      saeIntegration: route.saeIntegration ?? null,
      saeActions: route.saeActions ?? [],
    };
  }

  private buildSetupPayload(setup: IServiceWizardSetupPayload | null) {
    if (!setup) return null;
    return {
      name: setup.name ?? 'Default',
      fromDate: setup.fromDate ?? null,
      toDate: setup.toDate ?? null,
      config: setup.config ?? null,
      departures: setup.departures ?? [],
      daysOfWeek: setup.daysOfWeek ?? [],
      holyDays: setup.holyDays ?? [],
    };
  }
}
